import Accordion from 'react-bootstrap/Accordion';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Results, { hasVisibleResults } from './Results';
import Table from 'react-bootstrap/Table';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import ValueDisabled from './ValueDisabled';
import Help from './Help';

import axios from 'axios';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import './PlayerDecisionsTable.css';
import { GAME_STATUS } from './constants';
import { byCategoryOrdered } from './functions';

const TYPE_INTEGER = "INTEGER";
const TYPE_DOUBLE = "DOUBLE";
const TYPE_BOOLEAN = "BOOLEAN";

const submitDecisions = (decisions, gameId, round, auth, setError, onSuccess) => {
    axios.post(`/api/games/${gameId}/decisions/${round}`, decisions, {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      }
    })
      .then(response => {
        setError(undefined);
        onSuccess();
      })
      .catch(error => {
        console.log(error);
        setError('Failed to submit decisions. Make sure you have filled in all of them.');
      });
};

const ValueInput = ({ id, decision, formik }) => {
    return decision.type === 'INTEGER' || decision.type === 'DOUBLE' ?
        <Form.Control
            id={id}
            type="number"
            className="text-end"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[id]}
            isInvalid={formik.touched[id] && !!formik.errors[id]}
        /> :
        <Form.Check
            id={id}
            type="checkbox"
            onChange={(e) => formik.setFieldValue(id, e.target.checked)}
            onBlur={formik.handleBlur}
            checked={formik.values[id]}
        />;
};

const DecisionRow = ({ decision, status, formik, round }) => {
    const { t } = useTranslation();
    const id = decision.name;
    const help = t(`decisions.${decision.name}.description`) + (decision.price ? `(${decision.price}€)` : '');
    const unit = decision.unit ? '(' + t(`unit.${decision.unit}`) + ')' : '';
    return <tr key={decision.name}>
        <td key="name" className="align-middle">
            <Form.Label className="mb-0" title={help}>{t(`decisions.${decision.name}.label`)} {unit}</Form.Label>
        </td>
        {
            decision.values.map((value, i) => <td key={i} className="align-middle text-center">
                {
                    status === GAME_STATUS.ROUND_ACTIVE && round === i + 1 ?
                            <>
                                <ValueInput id={id} decision={decision} formik={formik} />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors[id]}
                                </Form.Control.Feedback>
                            </> :
                            <ValueDisabled type={decision.type} value={value} />

                }
            </td>)
        }
    </tr>
};

const SubcategoryDecisions = ({ category, subcategory, decisions, rounds, round, status, formik, results }) => {
    const { t } = useTranslation();
    let title = t(`categories.${category}.${subcategory}.title`);
    return <>
        <tr key={subcategory}>
            <td key="name" className="align-middle fw-bold" colSpan={round + 1}>
                <Form.Label className="mb-0">{title}</Form.Label>
            </td>
        </tr>
        {
            Object.values(decisions).map(decision => <DecisionRow key={decision.name} decision={decision} status={status} formik={formik} round={round} />)
        }
    </>
};

const CategoryDecisions = ({ category, subcategories, rounds, round, status, formik, results }) => {
    const { t } = useTranslation();
    let title = t(`categories.${category}.title`);
    const hasDecisions = Object.keys(subcategories).length > 0;
    return <Accordion.Item eventKey={category}>
        <Accordion.Header>
            { title }
        </Accordion.Header>
        <Accordion.Body>
            {hasDecisions &&
                <div>
                    <Help category={category} />
                    <div style={{ display: "inline-block" }}>
                        <Table striped>
                            <thead>
                                <tr>
                                    <th key="name" className="decisions-table-name-header">{t('game.decisions')}</th>
                                    {Array.from({ length: rounds }, (_, i) => {
                                        return <th key={i} className="decisions-table-header">{`${t('games.round')} ${i + 1}`}</th>;
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    Object.entries(subcategories).map(([subcategory, decisions]) => <SubcategoryDecisions
                                            key={subcategory}
                                            category={category}
                                            subcategory={subcategory}
                                            decisions={decisions}
                                            rounds={rounds}
                                            round={round}
                                            status={status}
                                            formik={formik}
                                            results={results}
                                        />)
                                }
                            </tbody>
                        </Table>
                    </div>
                </div>
            }
            <div>
                <Results results={results} category={category} rounds={rounds} round={round} />
            </div>
        </Accordion.Body>
    </Accordion.Item>;
};

const initialValues = (decisions) => {
    const round = Object.keys(decisions).length;
    const activeDecisions = decisions[round];
    const mapped = Object.values(activeDecisions).map(value => value);
    const result = mapped.reduce((acc, decision) => {
        if (decision.type === 'BOOLEAN') {
            const val = decision.value && decision.value === "true";
            acc[decision.name] = val;
        } else {
            acc[decision.name] = decision.value ? decision.value : '';
        }
        return acc;
    }, {});
    return result;
};

const validationSchema = (decisions) => {
    const round = Object.keys(decisions).length;
    const activeDecisions = decisions[round];
    const mapped = Object.values(activeDecisions).map(value => value);
    const result = mapped.reduce((schema, decision) => {
        if (decision.type === TYPE_INTEGER || decision.type === TYPE_DOUBLE) {
            var decisionSchema = Yup.number()
                .required('Required')
                .min(decision.min, `Must be between ${decision.min} and ${decision.max}`)
                .max(decision.max, `Must be between ${decision.min} and ${decision.max}`);
            if (decision.minWhenPresent !== undefined) {
                decisionSchema = decisionSchema.min(decision.min, `Either ${decision.min} or between ${decision.minWhenPresent} and ${decision.max}`);
                decisionSchema = decisionSchema.test('is-at-least', `Either ${decision.min} or between ${decision.minWhenPresent} and ${decision.max}`, (value) => {
                    if (value === 0 || value >= decision.minWhenPresent) return true;
                    return false;
                });
            } else {
            }
            schema[decision.name] = decisionSchema;
        }
      return schema;
    }, {});
    return Yup.object(result);
};

const fromDefinitions = (round, definitions) => {
    const decisions = definitions.reduce((acc, def) => {
        acc[def.name] = {
            ...def,
            value: '',
        }
        return acc;
    }, {});
    return {
        [round]: decisions
    };
};

const PlayerDecisionsTable = ({ decisionDefinitions, decisionValues, rounds, round, auth, id, status, results }) => {
    const team = sessionStorage.getItem("team");
    const { t } = useTranslation();
    const decisions = decisionValues?.decisions[team] || {};
    const roundsWithDecisions = Object.keys(decisions).length;
    const noDecisionsForCurrentRound = roundsWithDecisions < round;
    const populatedDecisions = noDecisionsForCurrentRound ?
        {...decisions, ...fromDefinitions(round, decisionDefinitions)} :
        decisions;
    const defaultActiveKeys = [];
    const [error, setError] = useState();
    const [toast, setToast] = useState();
    const decisionsByCategory = byCategoryOrdered(populatedDecisions, decisionDefinitions);
    const visibleCategories = Object.entries(decisionsByCategory).filter(([category, subcategories]) =>
        Object.keys(subcategories).length > 0 || hasVisibleResults(results, team, category)
    );
    const submit = (values, message) => {
        const submittedDecisions = Object.values(populatedDecisions[round]).map(def => {
            const val = def.type === TYPE_BOOLEAN ? values[def.name] || false : values[def.name];
            return {
                ...def,
                value: val,
            };
        });
        submitDecisions(submittedDecisions, id, round, auth, setError, () => setToast(message));
    };
    const formik = useFormik({
      initialValues: initialValues(populatedDecisions),
      validationSchema: validationSchema(populatedDecisions),
      onSubmit: values => submit(values, t('game.decisions-saved')),
    });

    return (
        <div>
            <Accordion defaultActiveKey={defaultActiveKeys}>
                {
                    visibleCategories.map(([category, subcategories]) =>
                        <CategoryDecisions
                            key={category}
                            category={category}
                            subcategories={subcategories}
                            rounds={rounds}
                            round={round}
                            status={status}
                            formik={formik}
                            results={results}
                        />
                    )
                }
            </Accordion>
            <ToastContainer className="p-3" position="top-center" containerPosition="fixed">
                <Toast
                    bg="success"
                    delay={3000}
                    autohide
                    show={!!toast}
                    onClose={() => setToast(undefined)}
                >
                    <Toast.Body className="text-white">{toast}</Toast.Body>
                </Toast>
            </ToastContainer>
            <div>
                {error && <Alert
                  className="mb-2"
                  variant="danger"
                  onClose={() => setError(undefined)}
                  dismissible
                >
                  {error}
                </Alert>}
            </div>
            <div className="mt-4">
                {
                    status === GAME_STATUS.ROUND_ACTIVE &&
                        <Card.Text className="text-end">
                            <Button
                                type="button"
                                variant="primary"
                                className="me-2"
                                onClick={() => submit(formik.values, t('game.decisions-draft-saved'))}
                            >{t('game.save-draft')}</Button>
                            <Button
                                type="button"
                                variant="primary"
                                className="me-2"
                                disabled={!(formik.isValid && formik.dirty)}
                                onClick={(event) => formik.handleSubmit(event)}
                            >{t('game.submit')}</Button>
                        </Card.Text>
                }
            </div>
        </div>
    );
};

export default PlayerDecisionsTable;
